import { useState } from 'react';
import { PiecesJointes } from './PiecesJointes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TYPES_EQUIPEMENT, ETATS_EQUIPEMENT } from '@eec/contrats';
import { CarteEquipements } from './CarteEquipements';
import { PrioritesPatrimoine } from './PrioritesPatrimoine';
import { SitesSensibles } from './SitesSensibles';
import { ImportPatrimoine } from './ImportPatrimoine';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { EnTetePage } from '../../commun/EnTetePage';
interface Equipement {
  id: string;
  code: string;
  nom: string;
  type: string;
  domaine: string;
  province_id: string;
  localisation: string;
  latitude: number;
  longitude: number;
  criticite: number;
  statut: keyof typeof ETATS_EQUIPEMENT;
  fabricant: string;
  caracteristiques: string;
  version: number;
}
export function PagePatrimoine() {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [formulaire, setFormulaire] = useState(false);
  const [edition, setEdition] = useState<Equipement | null>(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const liste = useQuery({
    queryKey: ['patrimoine', 'equipements'],
    queryFn: () => requete<{ elements: Equipement[] }>('/patrimoine/equipements'),
    enabled: peut(contexte, 'patrimoine', 'voir'),
  });
  const provinces = useQuery({
    queryKey: ['provinces'],
    queryFn: () => requete<{ elements: { id: string; nom: string }[] }>('/referentiel/provinces'),
    enabled: formulaire,
  });
  if (!peut(contexte, 'patrimoine', 'voir'))
    return <p role="alert">Accès au patrimoine non autorisé.</p>;
  return (
    <section>
      <EnTetePage titre="Patrimoine des réseaux" />
      {peut(contexte, 'patrimoine', 'creer') && <ImportPatrimoine key={contexte?.appartenance_active?.id} />}
      {peut(contexte, 'incident', 'voir') && <PrioritesPatrimoine />}
      <SitesSensibles equipements={liste.data?.elements ?? []} />
      {peut(contexte, 'patrimoine', 'creer') && (
        <button
          onClick={() => {
            setEdition(null);
            setFormulaire(true);
          }}
        >
          Ajouter un équipement
        </button>
      )}
      {formulaire && (
        <form
          key={edition?.id ?? 'nouveau'}
          style={{ display: 'grid', gap: 12, maxWidth: 700, marginBlock: 20 }}
          onSubmit={async (e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            setEnCours(true);
            setErreur('');
            const corps = {
              code: d.get('code'),
              nom: d.get('nom'),
              type: edition?.type ?? d.get('type'),
              province_id: d.get('province_id'),
              localisation: d.get('localisation'),
              latitude: Number(d.get('latitude')),
              longitude: Number(d.get('longitude')),
              criticite: Number(d.get('criticite')),
              statut: d.get('statut'),
              fabricant: d.get('fabricant'),
              caracteristiques: d.get('caracteristiques'),
              ...(edition ? { version: edition.version, motif: d.get('motif') } : {}),
            };
            try {
              await requete(
                edition ? `/patrimoine/equipements/${edition.id}` : '/patrimoine/equipements',
                { methode: edition ? 'PUT' : 'POST', corps },
              );
              setFormulaire(false);
              await client.invalidateQueries({ queryKey: ['patrimoine'] });
            } catch (err) {
              setErreur(messageErreur(err));
            } finally {
              setEnCours(false);
            }
          }}
        >
          <h2>{edition ? 'Modifier l’équipement' : 'Nouvel équipement'}</h2>
          <label>
            Code{' '}
            <input
              name="code"
              required
              pattern="[A-Z0-9-]{3,30}"
              readOnly={Boolean(edition)}
              defaultValue={edition?.code}
            />
          </label>
          <label>
            Nom{' '}
            <input name="nom" required minLength={3} maxLength={150} defaultValue={edition?.nom} />
          </label>
          <label>
            Type{' '}
            <select
              name="type"
              required
              disabled={Boolean(edition)}
              defaultValue={edition?.type ?? ''}
            >
              <option value="">Choisir</option>
              {TYPES_EQUIPEMENT.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.libelle} ({t.domaine === 'WATER' ? 'eau' : 'électricité'})
                </option>
              ))}
            </select>
          </label>
          <label>
            Province{' '}
            <select name="province_id" required defaultValue={edition?.province_id ?? ''}>
              <option value="">Choisir</option>
              {provinces.data?.elements.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nom}
                </option>
              ))}
            </select>
          </label>
          <label>
            Localisation{' '}
            <input
              name="localisation"
              required
              minLength={2}
              maxLength={250}
              defaultValue={edition?.localisation}
            />
          </label>
          <label>
            Latitude{' '}
            <input
              name="latitude"
              type="number"
              step="any"
              required
              min={-4}
              max={2.5}
              defaultValue={edition?.latitude}
            />
          </label>
          <label>
            Longitude{' '}
            <input
              name="longitude"
              type="number"
              step="any"
              required
              min={8}
              max={15}
              defaultValue={edition?.longitude}
            />
          </label>
          <label>
            Criticité (1 faible, 4 vitale){' '}
            <input
              name="criticite"
              type="number"
              required
              min={1}
              max={4}
              step={1}
              defaultValue={edition?.criticite ?? 2}
            />
          </label>
          <label>
            État{' '}
            <select name="statut" defaultValue={edition?.statut ?? 'IN_SERVICE'}>
              {Object.entries(ETATS_EQUIPEMENT).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label>
            Fabricant <input name="fabricant" maxLength={100} defaultValue={edition?.fabricant} />
          </label>
          <label>
            Caractéristiques{' '}
            <textarea
              name="caracteristiques"
              maxLength={4000}
              defaultValue={edition?.caracteristiques}
            />
          </label>
          {edition && (
            <label>
              Motif de modification <input name="motif" required minLength={3} maxLength={500} />
            </label>
          )}
          <button disabled={enCours || provinces.isPending}>Enregistrer l’équipement</button>
          <button type="button" onClick={() => setFormulaire(false)}>
            Annuler
          </button>
          {provinces.error && <p role="alert">{messageErreur(provinces.error)}</p>}
        </form>
      )}
      {erreur && <p role="alert">{erreur}</p>}
      {liste.error && <p role="alert">{messageErreur(liste.error)}</p>}
      {liste.isPending && <p role="status">Chargement…</p>}
      {liste.data && (
        <>
          <p>{liste.data.elements.length} équipement(s) affiché(s), maximum 500.</p>
          <CarteEquipements
            equipements={liste.data.elements}
            onSelection={(id) => {
              const fiche = document.getElementById(`equipement-${id}`);
              fiche?.scrollIntoView({ block: 'center' });
              fiche?.focus({ preventScroll: true });
            }}
          />
        </>
      )}
      {liste.data?.elements.map((e) => (
        <article
          id={`equipement-${e.id}`}
          tabIndex={-1}
          key={e.id}
          style={{ paddingBlock: 16, borderBottom: '1px solid currentColor' }}
        >
          <h2>
            {e.code} — {e.nom}
          </h2>
          <PiecesJointes
            type="equipement"
            id={e.id}
            ecriture={peut(contexte, 'patrimoine', 'modifier')}
          />
          <p>
            {ETATS_EQUIPEMENT[e.statut]} · criticité {e.criticite} · {e.localisation}
          </p>
          <p>
            {e.fabricant} · {e.caracteristiques}
          </p>
          {peut(contexte, 'patrimoine', 'modifier') && (
            <button
              onClick={() => {
                setEdition(e);
                setFormulaire(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Modifier l’équipement
            </button>
          )}
        </article>
      ))}
    </section>
  );
}
